import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Gift } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface TrialSubscriptionRow {
  plan_tier: string;
  status: string;
  trial_ends_at: string | null;
}

function useTrialSubscription(clubId: string | null) {
  return useQuery({
    queryKey: ["crm", "trial_subscription", clubId],
    enabled: clubId !== null,
    queryFn: async (): Promise<TrialSubscriptionRow | null> => {
      // P4-2 (#27): clube com histórico tem >1 assinatura; o maybeSingle direto
      // lançava. Pega a mais recente (reflete o estado atual do plano).
      const { data, error } = await supabase
        .from("club_subscriptions")
        .select("plan_tier, status, trial_ends_at")
        .eq("club_id", clubId!)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data as TrialSubscriptionRow | null) ?? null;
    },
    staleTime: 60_000,
    refetchInterval: 5 * 60_000, // 5 min — pega expiração sem precisar reload
  });
}

function formatCountdown(expiresAt: string, nowMs: number): {
  text: "hours" | "days";
  value: number;
} {
  const diff = Math.max(0, new Date(expiresAt).getTime() - nowMs);
  const hours = Math.floor(diff / (60 * 60 * 1000));
  if (hours <= 48) return { text: "hours", value: hours };
  return { text: "days", value: Math.floor(hours / 24) };
}

/**
 * Banner amarelo que aparece quando o clube atualmente selecionado está
 * em trial (status='trialing'). Mostra tier do trial + countdown amigável
 * (horas quando <48h, dias acima). Expira automaticamente pelo cron;
 * quando passa, o banner some no próximo refetch.
 */
export function TrialStatusBanner() {
  const { club } = useAuth();
  const { t, i18n } = useTranslation();
  const { data: sub } = useTrialSubscription(club?.id ?? null);
  const [now, setNow] = useState(() => Date.now());

  const isTrialing =
    sub?.status === "trialing" &&
    !!sub?.trial_ends_at &&
    new Date(sub.trial_ends_at).getTime() > now;

  useEffect(() => {
    if (!isTrialing) return;
    const tick = globalThis.setInterval(() => setNow(Date.now()), 60_000);
    return () => globalThis.clearInterval(tick);
  }, [isTrialing]);

  const countdown = useMemo(() => {
    if (!sub?.trial_ends_at) return null;
    return formatCountdown(sub.trial_ends_at, now);
  }, [sub?.trial_ends_at, now]);

  const expiresAtLabel = useMemo(() => {
    if (!sub?.trial_ends_at) return "";
    return new Intl.DateTimeFormat(i18n.language, {
      timeZone: "America/Sao_Paulo",
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(sub.trial_ends_at));
  }, [sub?.trial_ends_at, i18n.language]);

  if (!isTrialing || !sub || !countdown) return null;

  const planLabel = sub.plan_tier.toUpperCase();
  const countdownText =
    countdown.text === "hours"
      ? t("trialBanner.remainingHours", { count: countdown.value })
      : t("trialBanner.remainingDays", { count: countdown.value });

  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-yellow-100 dark:bg-yellow-900/30 border-b border-yellow-400 dark:border-yellow-700 text-yellow-900 dark:text-yellow-100 px-4 py-2 text-sm flex items-center gap-2"
      data-testid="trial-status-banner"
    >
      <Gift className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <p className="flex-1 min-w-0">
        <span className="font-semibold">
          {t("trialBanner.title", { plan: planLabel })}
        </span>
        <span className="ml-1 text-xs">
          {countdownText} · {t("trialBanner.expiresAt", { date: expiresAtLabel })}
        </span>
      </p>
    </div>
  );
}
